// Microbe v2 — the ONE persistent, always-on Canvas 2D world (default + only view).
//
// Grown from v1 dive.ts by *promoting the dive primitives to the permanent world* (design.md §6,
// DESIGN-v2.md Phase 1): the timer / endRun / reward handoff are gone; instead the loop runs
// forever, credits currency continuously (idle economy via applyProduction), and grows mass from
// every bite. The camera is an auto-zoom FOLLOW rig in WORLD space — as mass grows the organism's
// world radius grows but the camera pulls back to keep it ~28% of the screen, revealing more world
// (the agar.io/Osmos reveal, zero new tier-art). Death is never permadeath: at HP<=0 the cell
// ENCYSTS — a brief invulnerable respawn at the origin that skims a fraction of recent mass.
//
// Runs on kit primitives only: createLoop (fixed dt + render interpolation, pauses when hidden so
// offline time is NOT accrued here — main computes it from lastSeen) and attachInput (pointer-local
// drag, which we invert through the camera into world space). All state access goes through GameCtx.

import { createLoop, attachInput, haptic } from '../src/index.js';
import type { LoopHandle, InputHandle } from '../src/index.js';
import { applyProduction, MASS_START } from './state.js';
import type { GameCtx } from './types.js';

// ---- world / camera tuning ----
const R0 = 12; // cell world-radius at MASS_START
const BASE_SPEED = 95; // world units/s before the motility bonus
const APPROACH_K = 6; // ease gain toward the drag target
const FILL_FRAC = 0.15; // desired on-screen cell radius as a fraction of the smaller screen dim
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 6;
const CAM_LERP = 6; // camera position smoothing (per second)
const ZOOM_LERP = 2.5; // camera zoom smoothing (per second)

// ---- entity field ----
const MAX_ENT = 46;
const SPAWN_INTERVAL = 0.3;
const INIT_SCATTER = 26;

const FOOD_R = 7;
const VESICLE_R = 8;
const TOXIN_R = 10;
const O2_R = 16;

const TOXIN_DMG = 24; // reduced by membrane
const O2_DPS = 14; // per second while overlapping, * o2 multiplier

// ---- light pools (deterministic infinite grid so pools are stable as the camera pans) ----
const LIGHT_GRID = 360;
const LIGHT_R = 130;
const LIGHT_DENSITY = 0.45; // fraction of grid cells that carry a pool

// ---- misc ----
const ENCYST_INVULN_S = 2.0;
const ENCYST_MASS_PENALTY = 0.25; // fraction of mass-above-start lost on encyst
const TAU = Math.PI * 2;

const C_TEAL = '#4fd1b0';
const C_ACCENT = '#ffcf5c';
const C_DANGER = '#ff5a5a';

type EntKind = 'food' | 'vesicle' | 'toxin' | 'o2';
interface Ent {
  x: number;
  y: number;
  r: number;
  vx: number;
  vy: number;
  kind: EntKind;
}

function rand(min: number, max: number): number {
  return min + Math.random() * (max - min);
}
function clamp(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n;
}

// Stable 0..1 hash for the light grid (integer cell coords + a salt for each property).
function hash(ix: number, iy: number, salt: number): number {
  let h = (ix * 374761393 + iy * 668265263 + salt * 1442695040) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h = h ^ (h >>> 16);
  return (h >>> 0) / 4294967295;
}

/**
 * Mount the persistent world against a canvas. Returned handle: start() (re)inits run state and
 * starts the loop + input; stop() halts both (both idempotent). main.ts mounts this once at boot.
 */
export function mountWorld(
  canvas: HTMLCanvasElement,
  ctx: GameCtx,
): { start(): void; stop(): void } {
  const ctx2d = canvas.getContext('2d');
  if (!ctx2d) throw new Error('microbe/world: 2D canvas context unavailable');
  const g: CanvasRenderingContext2D = ctx2d;

  // ---- viewport (DPR + resize; render re-applies the transform every frame) ----
  let W = 0;
  let H = 0;
  let dpr = 1;
  function resize(): void {
    dpr = Math.min(window.devicePixelRatio || 1, 3);
    W = window.innerWidth;
    H = window.innerHeight;
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
  }

  // ---- camera (world space) ----
  const cam = { x: 0, y: 0, zoom: 1 };

  // ---- the one control point ----
  const cell = { x: 0, y: 0, px: 0, py: 0, vx: 0, vy: 0, hp: 0, flash: 0 };

  // drag target lives in SCREEN space (CSS px, element-local); inverted through the camera per
  // frame while dragging. onRelease freezes the last WORLD target so the cell keeps easing to it.
  const screenTarget = { x: 0, y: 0 };
  const target = { x: 0, y: 0 };
  let dragging = false;
  let hasTarget = false;

  // ---- run state ----
  const entities: Ent[] = [];
  let spawnTimer = 0;
  let hurtCd = 0; // throttles the O2-drain hurt sound/haptic
  let invuln = 0; // post-encyst invulnerability window
  let time = 0; // wobble / animation clock

  let loop: LoopHandle | null = null;
  let input: InputHandle | null = null;
  let running = false;

  // ---- derived helpers (recomputed live so popup purchases take effect immediately) ----
  const maxHP = (): number => 100 + 40 * ctx.lvl('membrane');
  const cellRadius = (): number => R0 * Math.sqrt(ctx.state().mass / MASS_START);
  const screenFillPx = (): number => FILL_FRAC * Math.min(W, H);
  const targetZoom = (): number => clamp(screenFillPx() / cellRadius(), ZOOM_MIN, ZOOM_MAX);

  // ---- light grid ----
  function forEachLightNear(cb: (x: number, y: number, r: number) => void): void {
    const hw = W / 2 / cam.zoom;
    const hh = H / 2 / cam.zoom;
    const minGX = Math.floor((cam.x - hw - LIGHT_R) / LIGHT_GRID);
    const maxGX = Math.floor((cam.x + hw + LIGHT_R) / LIGHT_GRID);
    const minGY = Math.floor((cam.y - hh - LIGHT_R) / LIGHT_GRID);
    const maxGY = Math.floor((cam.y + hh + LIGHT_R) / LIGHT_GRID);
    for (let gx = minGX; gx <= maxGX; gx++) {
      for (let gy = minGY; gy <= maxGY; gy++) {
        if (hash(gx, gy, 1) >= LIGHT_DENSITY) continue;
        const x = (gx + 0.5) * LIGHT_GRID + (hash(gx, gy, 2) - 0.5) * LIGHT_GRID * 0.6;
        const y = (gy + 0.5) * LIGHT_GRID + (hash(gx, gy, 3) - 0.5) * LIGHT_GRID * 0.6;
        const r = LIGHT_R * (0.7 + 0.6 * hash(gx, gy, 4));
        cb(x, y, r);
      }
    }
  }
  function inLight(x: number, y: number): boolean {
    let hit = false;
    forEachLightNear((lx, ly, lr) => {
      if (!hit && Math.hypot(x - lx, y - ly) < lr) hit = true;
    });
    return hit;
  }

  // ---- spawning ----
  function pickKind(): EntKind {
    const r = Math.random();
    if (r < 0.6) return 'food'; // common
    if (r < 0.78) return 'toxin';
    if (r < 0.93) return 'o2';
    return 'vesicle'; // rare
  }
  // edge=true spawns just beyond the viewport (ongoing top-up); edge=false scatters across the
  // visible area (initial fill) so the world isn't empty until the cell first moves.
  function spawnOne(edge: boolean): void {
    const hw = W / 2 / cam.zoom;
    const hh = H / 2 / cam.zoom;
    const ring = Math.max(hw, hh);
    const kind = pickKind();
    let r = FOOD_R;
    let vx = 0;
    let vy = 0;
    if (kind === 'vesicle') {
      r = VESICLE_R;
    } else if (kind === 'toxin') {
      r = TOXIN_R;
      const a = Math.random() * TAU;
      const sp = rand(18, 45);
      vx = Math.cos(a) * sp;
      vy = Math.sin(a) * sp;
    } else if (kind === 'o2') {
      r = O2_R;
    }
    const ang = Math.random() * TAU;
    const dist = edge ? ring * rand(1.05, 1.35) : rand(cellRadius() + r + 24, ring * 1.05);
    entities.push({ x: cam.x + Math.cos(ang) * dist, y: cam.y + Math.sin(ang) * dist, r, vx, vy, kind });
  }

  // ---- encyst (auto-respawn; NEVER permadeath, NEVER wipes the save) ----
  function encyst(): void {
    const mass = ctx.state().mass;
    const penalty = ENCYST_MASS_PENALTY * Math.max(0, mass - MASS_START);
    if (penalty > 0) ctx.addMass(-penalty); // ctx floors the loss; mass never drops below start
    cell.x = 0;
    cell.y = 0;
    cell.px = 0;
    cell.py = 0;
    cell.vx = 0;
    cell.vy = 0;
    cell.hp = maxHP();
    cell.flash = 0;
    target.x = 0;
    target.y = 0;
    hasTarget = false;
    dragging = false;
    invuln = ENCYST_INVULN_S;
    ctx.sfx('encyst');
    haptic([0, 40, 60, 40]);
  }

  // ---- simulation ----
  function update(dt: number): void {
    if (ctx.isPaused()) return; // soft-pause under the genome popup

    applyProduction(dt); // idle economy ticks with the world
    time += dt;
    if (cell.flash > 0) cell.flash = Math.max(0, cell.flash - dt * 4);
    if (hurtCd > 0) hurtCd = Math.max(0, hurtCd - dt);
    if (invuln > 0) invuln = Math.max(0, invuln - dt);

    // --- steer: invert the screen target through the camera, ease the cell toward it ---
    cell.px = cell.x;
    cell.py = cell.y;
    if (dragging) {
      target.x = (screenTarget.x - W / 2) / cam.zoom + cam.x;
      target.y = (screenTarget.y - H / 2) / cam.zoom + cam.y;
    }
    if (hasTarget) {
      const speedCap = BASE_SPEED * (1 + 0.12 * ctx.lvl('motility'));
      const dx = target.x - cell.x;
      const dy = target.y - cell.y;
      const dist = Math.hypot(dx, dy);
      if (dist > 0.5) {
        const sp = Math.min(dist * APPROACH_K, speedCap);
        cell.vx = (dx / dist) * sp;
        cell.vy = (dy / dist) * sp;
      } else {
        cell.vx = 0;
        cell.vy = 0;
      }
    }
    cell.x += cell.vx * dt;
    cell.y += cell.vy * dt;

    // --- spawn top-up (ring) up to the cap ---
    spawnTimer += dt;
    while (spawnTimer >= SPAWN_INTERVAL) {
      spawnTimer -= SPAWN_INTERVAL;
      if (entities.length < MAX_ENT) spawnOne(true);
    }

    // --- step + cull + collisions ---
    const cellR = cellRadius();
    const hw = W / 2 / cam.zoom;
    const hh = H / 2 / cam.zoom;
    const cullR = Math.max(hw, hh) * 1.7;
    const memb = ctx.lvl('membrane');
    const o2Mult = Math.max(0, 1 - ctx.lvl('o2Tolerance') / 3); // L0=1 (full hurt) … L3=0
    for (let i = entities.length - 1; i >= 0; i--) {
      const e = entities[i]!;
      if (e.vx !== 0 || e.vy !== 0) {
        e.x += e.vx * dt;
        e.y += e.vy * dt;
      }
      if (Math.hypot(e.x - cam.x, e.y - cam.y) > cullR) {
        entities.splice(i, 1);
        continue;
      }
      if (Math.hypot(e.x - cell.x, e.y - cell.y) >= cellR + e.r) continue;

      switch (e.kind) {
        case 'food':
          ctx.credit({ energy: 6, biomass: 3, xp: 2 });
          ctx.addMass(0.5);
          ctx.sfx('eat');
          haptic(8);
          entities.splice(i, 1);
          break;
        case 'vesicle':
          ctx.credit({ enzymes: 1, xp: 5 });
          ctx.addMass(0.3);
          ctx.sfx('vesicle');
          haptic([0, 12, 30, 12]);
          entities.splice(i, 1);
          break;
        case 'toxin':
          entities.splice(i, 1);
          if (invuln <= 0) {
            cell.hp -= TOXIN_DMG * Math.max(0.2, 1 - 0.12 * memb);
            cell.flash = 1;
            ctx.sfx('hurt');
            haptic(20);
            if (cell.hp <= 0) encyst();
          }
          break;
        case 'o2':
          if (invuln <= 0 && o2Mult > 0) {
            cell.hp -= O2_DPS * o2Mult * dt;
            cell.flash = Math.max(cell.flash, 0.4);
            if (hurtCd <= 0) {
              hurtCd = 0.5;
              ctx.sfx('hurt');
              haptic(15);
            }
            if (cell.hp <= 0) encyst();
          }
          break;
      }
    }

    // --- photosynthesis regen inside light pools; clamp HP to the live max ---
    const photo = ctx.lvl('photosynthesis');
    if (invuln <= 0 && photo > 0 && inLight(cell.x, cell.y)) {
      cell.hp = Math.min(maxHP(), cell.hp + 3 * photo * dt);
    }
    cell.hp = Math.min(cell.hp, maxHP());

    // --- camera: lerp position to the cell, zoom to keep the cell ~FILL_FRAC of the screen ---
    const kPos = 1 - Math.exp(-CAM_LERP * dt);
    const kZoom = 1 - Math.exp(-ZOOM_LERP * dt);
    cam.x += (cell.x - cam.x) * kPos;
    cam.y += (cell.y - cam.y) * kPos;
    cam.zoom += (targetZoom() - cam.zoom) * kZoom;
  }

  // ---- render ----
  function render(alpha: number): void {
    if (W === 0 || H === 0) return;

    // reset to device pixels; everything below is authored in CSS px until the camera transform.
    g.setTransform(dpr, 0, 0, dpr, 0, 0);

    // dark radial water (screen space)
    const bg = g.createRadialGradient(W / 2, H * 0.42, 0, W / 2, H * 0.5, Math.max(W, H) * 0.8);
    bg.addColorStop(0, '#12202a');
    bg.addColorStop(1, '#070b0f');
    g.fillStyle = bg;
    g.fillRect(0, 0, W, H);

    // enter world space
    g.save();
    g.translate(W / 2, H / 2);
    g.scale(cam.zoom, cam.zoom);
    g.translate(-cam.x, -cam.y);

    // warm light pools
    forEachLightNear((lx, ly, lr) => {
      const grad = g.createRadialGradient(lx, ly, 0, lx, ly, lr);
      grad.addColorStop(0, 'rgba(255,207,92,0.16)');
      grad.addColorStop(1, 'rgba(255,207,92,0)');
      g.fillStyle = grad;
      g.beginPath();
      g.arc(lx, ly, lr, 0, TAU);
      g.fill();
    });

    for (const e of entities) drawEntity(e);

    const ix = cell.px + (cell.x - cell.px) * alpha;
    const iy = cell.py + (cell.y - cell.py) * alpha;
    drawCell(ix, iy);

    g.restore();
  }

  function drawEntity(e: Ent): void {
    switch (e.kind) {
      case 'food': {
        const grad = g.createRadialGradient(e.x, e.y, 0, e.x, e.y, e.r);
        grad.addColorStop(0, '#cfe89a');
        grad.addColorStop(0.6, '#9ed35a');
        grad.addColorStop(1, 'rgba(158,211,90,0.15)');
        g.fillStyle = grad;
        g.beginPath();
        g.arc(e.x, e.y, e.r, 0, TAU);
        g.fill();
        break;
      }
      case 'vesicle': {
        const glow = g.createRadialGradient(e.x, e.y, 0, e.x, e.y, e.r * 2.2);
        glow.addColorStop(0, 'rgba(255,207,92,0.55)');
        glow.addColorStop(1, 'rgba(255,207,92,0)');
        g.fillStyle = glow;
        g.beginPath();
        g.arc(e.x, e.y, e.r * 2.2, 0, TAU);
        g.fill();
        g.fillStyle = C_ACCENT;
        g.beginPath();
        g.arc(e.x, e.y, e.r, 0, TAU);
        g.fill();
        g.fillStyle = 'rgba(255,255,255,0.7)';
        g.beginPath();
        g.arc(e.x - e.r * 0.3, e.y - e.r * 0.3, e.r * 0.28, 0, TAU);
        g.fill();
        break;
      }
      case 'toxin': {
        g.fillStyle = 'rgba(224,92,255,0.85)';
        g.beginPath();
        const spikes = 8;
        for (let k = 0; k < spikes * 2; k++) {
          const rr = k % 2 === 0 ? e.r : e.r * 0.55;
          const ang = (Math.PI * k) / spikes + time * 0.6;
          const px = e.x + Math.cos(ang) * rr;
          const py = e.y + Math.sin(ang) * rr;
          if (k === 0) g.moveTo(px, py);
          else g.lineTo(px, py);
        }
        g.closePath();
        g.fill();
        g.fillStyle = 'rgba(20,6,26,0.6)';
        g.beginPath();
        g.arc(e.x, e.y, e.r * 0.3, 0, TAU);
        g.fill();
        break;
      }
      case 'o2': {
        g.strokeStyle = 'rgba(150,200,255,0.85)';
        g.lineWidth = 2;
        g.beginPath();
        g.arc(e.x, e.y, e.r, 0, TAU);
        g.stroke();
        g.fillStyle = 'rgba(150,200,255,0.10)';
        g.fill();
        g.fillStyle = 'rgba(255,255,255,0.5)';
        g.beginPath();
        g.arc(e.x - e.r * 0.3, e.y - e.r * 0.3, e.r * 0.16, 0, TAU);
        g.fill();
        break;
      }
    }
  }

  function drawCell(cx: number, cy: number): void {
    const r = cellRadius();
    const mhp = maxHP();
    const hurt = cell.flash > 0;
    g.save();
    g.translate(cx, cy);

    // encyst shell (invulnerable respawn visual)
    if (invuln > 0) {
      const pulse = 0.5 + 0.5 * Math.sin(time * 8);
      g.strokeStyle = `rgba(180,220,255,${0.4 + 0.3 * pulse})`;
      g.lineWidth = r * 0.14;
      g.beginPath();
      g.arc(0, 0, r * 1.28, 0, TAU);
      g.stroke();
      g.strokeStyle = 'rgba(180,220,255,0.18)';
      g.lineWidth = r * 0.06;
      g.beginPath();
      g.arc(0, 0, r * 1.5, 0, TAU);
      g.stroke();
    }

    // wobbling membrane blob
    const N = 28;
    g.beginPath();
    for (let i = 0; i <= N; i++) {
      const a = (i / N) * TAU;
      const rr = r * (1 + 0.05 * Math.sin(a * 3 + time * 2.2) + 0.03 * Math.sin(a * 5 - time * 1.7));
      const px = Math.cos(a) * rr;
      const py = Math.sin(a) * rr;
      if (i === 0) g.moveTo(px, py);
      else g.lineTo(px, py);
    }
    g.closePath();
    const grad = g.createRadialGradient(0, 0, r * 0.2, 0, 0, r);
    grad.addColorStop(0, hurt ? 'rgba(255,120,120,0.95)' : 'rgba(120,235,205,0.95)');
    grad.addColorStop(1, hurt ? 'rgba(200,60,60,0.65)' : 'rgba(60,180,155,0.6)');
    g.fillStyle = grad;
    g.fill();
    g.lineWidth = Math.max(1, r * 0.05);
    g.strokeStyle = hurt ? C_DANGER : C_TEAL;
    g.stroke();

    // cytoplasm + nucleus
    g.fillStyle = 'rgba(8,14,18,0.45)';
    g.beginPath();
    g.arc(0, 0, r * 0.6, 0, TAU);
    g.fill();
    g.fillStyle = C_ACCENT;
    g.beginPath();
    g.arc(r * 0.12, r * 0.08, r * 0.26, 0, TAU);
    g.fill();

    g.restore();

    // HP ring around the cell (only while damaged — readable on a phone without a HUD)
    if (cell.hp < mhp - 0.01) {
      const frac = clamp(cell.hp / mhp, 0, 1);
      g.save();
      g.translate(cx, cy);
      g.lineWidth = Math.max(1.5, r * 0.09);
      g.strokeStyle = 'rgba(255,255,255,0.10)';
      g.beginPath();
      g.arc(0, 0, r * 1.18, 0, TAU);
      g.stroke();
      g.strokeStyle = frac > 0.5 ? C_TEAL : frac > 0.25 ? C_ACCENT : C_DANGER;
      g.beginPath();
      g.arc(0, 0, r * 1.18, -Math.PI / 2, -Math.PI / 2 + frac * TAU);
      g.stroke();
      g.restore();
    }
  }

  function initRun(): void {
    resize();
    cam.x = 0;
    cam.y = 0;
    cell.x = 0;
    cell.y = 0;
    cell.px = 0;
    cell.py = 0;
    cell.vx = 0;
    cell.vy = 0;
    cell.hp = maxHP();
    cell.flash = 0;
    target.x = 0;
    target.y = 0;
    dragging = false;
    hasTarget = false;
    invuln = 0;
    entities.length = 0;
    spawnTimer = 0;
    hurtCd = 0;
    time = 0;
    cam.zoom = targetZoom(); // snap so the first frame isn't a zoom-in from a default
    for (let i = 0; i < INIT_SCATTER; i++) spawnOne(false);
  }

  return {
    start(): void {
      if (running) return;
      running = true;
      initRun();
      window.addEventListener('resize', resize);
      input = attachInput(canvas, {
        onPress: (p) => {
          dragging = true;
          hasTarget = true;
          screenTarget.x = p.x;
          screenTarget.y = p.y;
        },
        onMove: (p) => {
          screenTarget.x = p.x;
          screenTarget.y = p.y;
        },
        onRelease: () => {
          dragging = false; // keep the last (now frozen) world target
        },
      });
      loop = createLoop({ update, render });
      loop.start();
    },
    stop(): void {
      if (!running) return;
      running = false;
      loop?.stop();
      loop = null;
      input?.destroy();
      input = null;
      window.removeEventListener('resize', resize);
    },
  };
}
